(function () {
  const config = window.SimplStockRegionalStock;
  if (!config || config.productHandle !== 'ventura-ii') return;

  const endpoint = config.endpoint || '/apps/simplstock/availability';
  const staticDataUrl = config.staticDataUrl || '';
  const productId = String(config.productId || '');
  const productHandle = config.productHandle;
  const country = String(config.country || window.Shopify?.country || '').trim().toUpperCase();
  const addToCartText = config.addToCartText || 'Add to cart';
  const regionalSoldOutText = config.regionalSoldOutText || 'Sold out';
  const regionalQuantityText = config.regionalQuantityText || 'Only [quantity] available for your region';
  const shippingUnavailableText = 'Shipping temporarily unavailable';
  const shippingUnavailableNote = 'We are temporarily unable to ship Ventura II to your selected country. Please choose a supported delivery country.';
  const initialSearch = new URL(window.location.href).searchParams;

  const state = {
    payload: null,
    failed: false,
    cartQuantities: new Map(),
    penData: null,
    defaultSelectionApplied: false,
    // Explicit ad/deep links and Shopify option-value links must keep their selection.
    defaultSelectionLocked: initialSearch.has('variant') || initialSearch.has('option_values'),
  };

  function injectStyles() {
    if (document.getElementById('simplstock-regional-stock-style')) return;
    const style = document.createElement('style');
    style.id = 'simplstock-regional-stock-style';
    style.textContent = `
      .simplstock-region-note {
        display: none;
        width: 100%;
        margin-top: var(--margin-xs, 8px);
        color: rgb(var(--color-foreground-rgb) / var(--opacity-80, 0.8));
        font-size: var(--font-size--sm, 0.875rem);
        text-align: center;
      }

      .simplstock-region-note.is-visible {
        display: block;
      }
    `;
    document.head.appendChild(style);
  }

  function getProductForms() {
    if (!productId) return Array.from(document.querySelectorAll('product-form-component'));
    return Array.from(document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`));
  }

  function getCurrentVariantId() {
    const formInput = document.querySelector('product-form-component input[name="id"]');
    if (formInput?.value) return String(formInput.value);

    const sticky = document.querySelector('sticky-add-to-cart[data-current-variant-id]');
    if (sticky?.dataset.currentVariantId) return String(sticky.dataset.currentVariantId);

    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    return urlVariant ? String(urlVariant) : '';
  }

  function getVariantStatus(variantId) {
    if (!variantId || !state.payload?.variants) return null;
    const regional = state.payload.variants[String(variantId)];
    if (!regional) return null;
    const nativeVariant = readPenData()?.variants?.find((variant) => String(variant.id) === String(variantId));
    // A warehouse snapshot must never re-enable a variant Shopify marks sold out.
    if (nativeVariant?.available === false) {
      return { ...regional, available: false, quantity: 0 };
    }
    // The regional snapshot can age between refreshes. For tracked, deny-policy
    // stock, never let its quantity exceed Shopify's current page-render total.
    // This only lowers a limit; it cannot enable a blocked country or variant.
    const nativeQuantity = nativeVariant?.inventory_quantity;
    if (nativeVariant?.inventory_management && nativeVariant.inventory_policy === 'deny' &&
        typeof nativeQuantity === 'number' && Number.isFinite(nativeQuantity)) {
      const regionalQuantity = Math.max(0, Number(regional.quantity) || 0);
      const quantity = Math.min(regionalQuantity, Math.max(0, nativeQuantity));
      return { ...regional, quantity, available: regional.available === true && quantity > 0 };
    }
    return regional;
  }

  function payloadForCountry(staticData) {
    if (!staticData?.variants || !staticData?.countryLocationMap) return null;

    const shippingBlocked = (staticData.shippingBlockedCountries || []).includes(country);
    const eligibleLocationIds = shippingBlocked ? [] :
      staticData.countryLocationMap[country] ||
      staticData.countryLocationMap.REST_OF_WORLD ||
      [];
    if (!eligibleLocationIds.length && !shippingBlocked) return null;

    const eligibleLocationSet = new Set(eligibleLocationIds.map(String));
    const variants = {};

    for (const [variantId, variant] of Object.entries(staticData.variants)) {
      let quantity = 0;
      const availableLocationIds = [];

      for (const [locationId, rawQuantity] of Object.entries(variant.locationQuantities || {})) {
        if (!eligibleLocationSet.has(String(locationId))) continue;
        const sellableQuantity = Math.max(0, Number(rawQuantity) || 0);
        quantity += sellableQuantity;
        if (sellableQuantity > 0) availableLocationIds.push(String(locationId));
      }

      variants[variantId] = {
        title: variant.title,
        options: variant.options || [],
        available: quantity > 0,
        quantity,
        eligibleLocationIds,
        availableLocationIds,
        shippingBlocked,
      };
    }

    return {
      ok: true,
      handle: staticData.productHandle || productHandle,
      productTitle: staticData.productTitle || '',
      country,
      shippingBlocked,
      eligibleLocations: eligibleLocationIds.map((id) => ({
        id: String(id),
        name: staticData.locations?.[id] || String(id),
      })),
      generatedAt: staticData.generatedAt,
      variants,
    };
  }

  function getQuantityRequested(form) {
    const input = form.querySelector('quantity-selector-component input[ref="quantityInput"], input[name="quantity"]');
    const quantity = Number.parseInt(input?.value || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function getCartQuantity(variantId) {
    return state.cartQuantities.get(String(variantId)) || 0;
  }

  function isQuantityBlocked(status, variantId, form) {
    if (!status?.available) return false;
    const requested = getQuantityRequested(form);
    return requested + getCartQuantity(variantId) > status.quantity;
  }

  function setButtonLabel(button, label) {
    const labelElement =
      button?.querySelector('[data-add-to-cart-label]') ||
      button?.querySelector('.add-to-cart-text__content > span:first-child') ||
      button?.querySelector('.add-to-cart-text__content');
    if (labelElement) labelElement.textContent = label;
  }

  function ensureNote(formComponent) {
    let note = formComponent.querySelector('.simplstock-region-note');
    if (note) return note;

    note = document.createElement('div');
    note.className = 'simplstock-region-note';
    note.setAttribute('aria-live', 'polite');

    const productFormButtons = formComponent.querySelector('[ref="productFormButtons"], .product-form-buttons');
    productFormButtons?.insertAdjacentElement('afterend', note);
    return note;
  }

  function setNote(formComponent, message) {
    const note = ensureNote(formComponent);
    if (!note) return;
    note.textContent = message || '';
    note.classList.toggle('is-visible', Boolean(message));
  }

  function setFormAvailability(formComponent) {
    const variantInput = formComponent.querySelector('input[name="id"]');
    const variantId = variantInput?.value ? String(variantInput.value) : getCurrentVariantId();
    const status = getVariantStatus(variantId);
    if (!status) return;

    const addToCartButton = formComponent.querySelector('[ref="addToCartButton"]');
    const acceleratedCheckout = formComponent.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    const quantityBlocked = isQuantityBlocked(status, variantId, formComponent);
    const unavailable = !status.available;

    formComponent.dataset.simplstockRegionalAvailable = unavailable || quantityBlocked ? 'false' : 'true';
    // Quantity overage is not a sold-out variant. Keep its quantity selector
    // visible so shoppers can lower the amount without reloading the page.
    formComponent.dataset.variantAvailable = unavailable ? 'false' : 'true';

    if (addToCartButton) {
      addToCartButton.disabled = unavailable || quantityBlocked;
      if (unavailable) {
        setButtonLabel(addToCartButton, status.shippingBlocked ? shippingUnavailableText : regionalSoldOutText);
      } else if (quantityBlocked) {
        setButtonLabel(addToCartButton, regionalQuantityText.replace('[quantity]', status.quantity));
      } else {
        setButtonLabel(addToCartButton, addToCartText);
      }
    }

    if (acceleratedCheckout) {
      if (unavailable || quantityBlocked) {
        acceleratedCheckout.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckout.removeAttribute('hidden');
      }
    }

    if (unavailable) {
      setNote(formComponent, status.shippingBlocked ? shippingUnavailableNote : regionalSoldOutText);
    } else if (quantityBlocked) {
      setNote(formComponent, regionalQuantityText.replace('[quantity]', status.quantity));
    } else {
      setNote(formComponent, '');
    }
  }

  function setStickyAvailability() {
    const stickyBars = Array.from(document.querySelectorAll('sticky-add-to-cart'));
    const variantId = getCurrentVariantId();
    const status = getVariantStatus(variantId);
    if (!status) return;

    const form = getProductForms().find((component) =>
      String(component.querySelector('input[name="id"]')?.value) === variantId
    );
    const quantityBlocked = form ? isQuantityBlocked(status, variantId, form) : getCartQuantity(variantId) + 1 > status.quantity;
    const unavailable = !status.available || quantityBlocked;
    for (const sticky of stickyBars) {
      if (productId && sticky.dataset.productId !== productId) continue;
      const button = sticky.querySelector('[ref="addToCartButton"]');
      if (!button) continue;

      sticky.dataset.simplstockRegionalAvailable = unavailable ? 'false' : 'true';
      sticky.dataset.variantAvailable = status.available ? 'true' : 'false';
      button.disabled = unavailable;
      setButtonLabel(button, !status.available ? (status.shippingBlocked ? shippingUnavailableText : regionalSoldOutText) : quantityBlocked
        ? regionalQuantityText.replace('[quantity]', status.quantity) : addToCartText);
    }
  }

  function readPenData() {
    if (state.penData) return state.penData;
    const script = document.querySelector('script[data-pen-product-variants]');
    if (!script?.textContent) return null;
    try {
      state.penData = JSON.parse(script.textContent);
      return state.penData;
    } catch (_error) {
      return null;
    }
  }

  function currentOptionsFromVariantId(variantId) {
    const data = readPenData();
    if (!data?.variants) return [];
    return data.variants.find((variant) => String(variant.id) === String(variantId))?.options || [];
  }

  function chooseCliplessDefault(data, currentVariantId, statusForVariant) {
    if (!Array.isArray(data?.variants) || !Array.isArray(data?.options)) return null;
    const normalize = (value) => String(value || '').trim().toLowerCase();
    const nibIndex = data.options.findIndex((name) => normalize(name).includes('nib'));
    const colorIndex = data.options.findIndex((name) => normalize(name).includes('color'));
    const clipIndex = data.options.findIndex((name) => normalize(name).includes('clip'));
    if ([nibIndex, colorIndex, clipIndex].some((index) => index < 0)) return null;
    const isClipless = (value) => ['clipless', 'without clip', 'no clip', 'none'].includes(normalize(value));
    const current = data.variants.find((variant) => String(variant.id) === String(currentVariantId));
    const choices = new Map();
    for (const variant of data.variants) {
      const options = variant.options;
      if (!options || !isClipless(options[clipIndex]) || variant.available === false) continue;
      const status = statusForVariant(variant.id);
      if (status?.available !== true || !(Number(status.quantity) > 0)) continue;
      const nib = options[nibIndex];
      if (!nib || !options[colorIndex]) continue;
      if (!choices.has(nib)) choices.set(nib, { nib, colors: new Set(), variants: [] });
      const choice = choices.get(nib);
      choice.colors.add(options[colorIndex]);
      choice.variants.push(variant);
    }
    // Fine is a familiar everyday nib; prefer it only when color coverage ties.
    const isFine = (nib) => ['fine', 'f'].includes(normalize(nib));
    const ranked = [...choices.values()].sort((left, right) =>
      right.colors.size - left.colors.size ||
      Number(isFine(right.nib)) - Number(isFine(left.nib)) ||
      Number(right.nib === current?.options?.[nibIndex]) - Number(left.nib === current?.options?.[nibIndex])
    );
    const best = ranked[0];
    if (!best) return null; // No stock or shipping unsupported: never invent a sellable variant.
    return best.variants.find((variant) => variant.options[colorIndex] === current?.options?.[colorIndex]) || best.variants[0];
  }

  function applyInitialCliplessDefault() {
    if (state.defaultSelectionApplied || state.defaultSelectionLocked || !state.payload) return;
    const script = document.querySelector('script[data-pen-product-variants]');
    const pen = script?.parentElement;
    const picker = pen?.getThemeVariantPicker?.();
    // The native custom element must be upgraded before dispatching its change event.
    if (typeof picker?.updateSelectedOption !== 'function' || typeof picker?.variantChanged !== 'function') return;
    const desired = chooseCliplessDefault(readPenData(), getCurrentVariantId(), getVariantStatus);
    if (!desired) return;
    const controls = desired.options.map((value, index) => {
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${index}"]`);
      const radio = Array.from(fieldset?.querySelectorAll('input[type="radio"]') || [])
        .find((input) => String(input.value) === String(value));
      if (radio) return { element: radio, value };
      const select = picker.querySelectorAll('select')[index];
      return select && Array.from(select.options).some((option) => String(option.value) === String(value))
        ? { element: select, value } : null;
    });
    if (!controls.length || controls.some((control) => !control)) return;
    state.defaultSelectionApplied = true;
    if (String(desired.id) === getCurrentVariantId()) return;
    // Set all three native options, then request ONE normal theme update. This keeps
    // price, media, URL, sticky button and cart variant bound to the same real variant.
    for (const { element, value } of controls) {
      if (element instanceof HTMLSelectElement) element.value = String(value);
      picker.updateSelectedOption(element);
    }
    pen.writeDebugSelectionFromOptions?.(desired.options);
    const trigger = controls[controls.length - 1].element;
    if (trigger instanceof HTMLSelectElement) {
      trigger.options[trigger.selectedIndex].dataset.variantId = String(desired.id);
    } else {
      trigger.dataset.variantId = String(desired.id);
    }
    trigger.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function updatePenPickerAvailability() {
    const data = readPenData();
    if (!data?.variants || !state.payload?.variants) return;

    const currentOptions = currentOptionsFromVariantId(getCurrentVariantId());
    if (!currentOptions.length) return;

    const optionNames = data.options || [];
    const optionTargets = [
      { index: optionNames.findIndex((name) => String(name).toLowerCase().includes('color')), selector: '[data-ai-color-col]' },
      { index: optionNames.findIndex((name) => String(name).toLowerCase().includes('nib')), selector: '[data-ai-nib-buttons]' },
      { index: optionNames.findIndex((name) => String(name).toLowerCase().includes('clip')), selector: '[data-ai-clip-buttons]' },
    ];

    for (const target of optionTargets) {
      if (target.index < 0) continue;

      const unavailableValues = new Set();
      const values = new Set(data.variants.map((variant) => variant.options?.[target.index]).filter(Boolean));

      for (const value of values) {
        const hasAvailableMatch = data.variants.some((variant) => {
          if (!variant?.options) return false;
          if (String(variant.options[target.index]) !== String(value)) return false;
          for (let index = 0; index < currentOptions.length; index++) {
            if (index === target.index) continue;
            if (String(variant.options[index]) !== String(currentOptions[index])) return false;
          }
          return getVariantStatus(variant.id)?.available === true;
        });

        if (!hasAvailableMatch) unavailableValues.add(String(value).trim().toLowerCase());
      }

      for (const container of document.querySelectorAll(target.selector)) {
        for (const button of Array.from(container.children)) {
          const value = String(button.dataset.value || '').trim().toLowerCase();
          const unavailable = unavailableValues.has(value);
          button.classList.toggle('is-unavailable', unavailable);
          button.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        }
      }
    }
  }

  function refreshRegionalState() {
    if (!state.payload) return;
    applyInitialCliplessDefault();
    for (const formComponent of getProductForms()) {
      setFormAvailability(formComponent);
    }
    setStickyAvailability();
    updatePenPickerAvailability();
  }

  function handleSubmit(event) {
    if (!state.payload) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const formComponent = form.closest('product-form-component');
    if (!formComponent) return;

    const variantId = form.querySelector('input[name="id"]')?.value;
    const status = getVariantStatus(variantId);
    if (!status) return;

    const quantityBlocked = isQuantityBlocked(status, variantId, formComponent);
    if (status.available && !quantityBlocked) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    setFormAvailability(formComponent);
  }

  async function refreshCartQuantities() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      const quantities = new Map();
      for (const item of cart.items || []) {
        quantities.set(String(item.variant_id), (quantities.get(String(item.variant_id)) || 0) + item.quantity);
      }
      state.cartQuantities = quantities;
      refreshRegionalState();
    } catch (_error) {
      state.cartQuantities = new Map();
    }
  }

  async function fetchAvailability() {
    if (!country) return;

    if (config.preferStatic && staticDataUrl) {
      try {
        const response = await fetch(staticDataUrl, { headers: { Accept: 'application/json' } });
        const payload = payloadForCountry(await response.json());
        if (!response.ok || !payload?.ok) throw new Error('Regional stock unavailable');
        state.payload = payload;
        state.failed = false;
        refreshRegionalState();
      } catch (_error) {
        state.failed = true;
      }
      return;
    }

    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('handle', productHandle);
    url.searchParams.set('country', country);

    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      const payload = await response.json();
      if (!response.ok || !payload?.ok) throw new Error(payload?.error || 'Regional stock unavailable');
      state.payload = payload;
      state.failed = false;
      refreshRegionalState();
    } catch (error) {
      if (!staticDataUrl) {
        state.failed = true;
        return;
      }

      try {
        const response = await fetch(staticDataUrl, { headers: { Accept: 'application/json' } });
        const staticData = await response.json();
        const payload = payloadForCountry(staticData);
        if (!response.ok || !payload?.ok) throw new Error('Static regional stock unavailable');
        state.payload = payload;
        state.failed = false;
        refreshRegionalState();
      } catch (_staticError) {
        state.failed = true;
      }
    }
  }

  function bindEvents() {
    document.addEventListener('submit', handleSubmit, true);

    // A shopper can start choosing before the stock request finishes. Never
    // override that choice when its response arrives, or on later cart updates.
    const lockInitialChoice = (event) => {
      if (!event.isTrusted || !(event.target instanceof Element)) return;
      if (event.target.closest('[data-ai-picker], variant-picker, [data-pen-click-area]')) {
        state.defaultSelectionLocked = true;
      }
    };
    document.addEventListener('pointerdown', lockInitialChoice, true);
    document.addEventListener('keydown', lockInitialChoice, true);

    const scheduleRefresh = () => {
      window.setTimeout(refreshRegionalState, 0);
      window.setTimeout(refreshRegionalState, 80);
      window.setTimeout(refreshRegionalState, 250);
      window.setTimeout(refreshRegionalState, 600);
    };

    document.addEventListener('variant:update', scheduleRefresh);
    document.addEventListener('variant:selected', scheduleRefresh);
    document.addEventListener('change', scheduleRefresh, true);
    document.addEventListener('input', scheduleRefresh, true);
    document.addEventListener('cart:update', refreshCartQuantities);

    const mutationRoot = document.querySelector('main') || document.body;
    let mutationRefreshTimer = null;
    const observer = new MutationObserver(() => {
      if (!state.payload) return;
      window.clearTimeout(mutationRefreshTimer);
      mutationRefreshTimer = window.setTimeout(refreshRegionalState, 30);
    });
    observer.observe(mutationRoot, { childList: true, subtree: true });
  }

  injectStyles();
  bindEvents();
  window.customElements?.whenDefined('variant-picker').then(refreshRegionalState);
  refreshCartQuantities();
  fetchAvailability();
})();
